import React, { cloneElement, createContext, useReducer } from 'react'
import { MemoryRouter, Switch, Route, Redirect, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { pathToRegexp } from 'path-to-regexp'

import { Grid } from '@material-ui/core'
import { useTheme } from '@material-ui/styles'

import Cookies from '~/cookie'
import DrawerProduct from '~/components/DrawerProduct'
import OnlineStoreEmbed from '~/components/OnlineStore'
import useViewportSize from '~/components/useViewportSize'
import AnonymousMode from '~/components/AnonymousMode'

import PositionHandler, { usePositionHandler } from './containers/PositionHandler'
import IndexPage from './containers/IndexPage'
import SearchPage from './containers/SearchPage'
import StoresPage from './containers/StoresPage'
import ShopBasketPage from './containers/ShopBasketPage'
import ProductDetailsPage from './containers/ProductDetailsPage'
import Snack from './containers/Snack'
import Dialog from './containers/Dialog'
import StoreInformations from './containers/StoreInformations'
import StorePage from './containers/StorePage'

import { TYPES } from '~/actions'
import { getDomain } from '~/utils'
import Terms from './containers/Terms'
import { viewSelector } from './selectors'

const routersWhiteList = pathToRegexp(
  [
    '/',
    '/termos',
    '/sacola/:gtins',
    '/produto/:gtin/detalhes',
    '/produto/:fakeName/:gtin/detalhes',
    '/loja/:cnpj/produtos',
    '/produto/:fakeName/:gtin',
    '/produto/:gtin',
    '/busca',
    '/onlineStore',
    '/loja/:cnpj',
    '/produto/:gtin/detalhes'
  ],
  null,
  { sensitive: true }
)

export const AppRouterContext = createContext({})

function reducer(state, { type, ...payload }) {
  return { ...state, ...payload }
}

const getMapSize = (viewport) => viewport.height * 0.4

export default function RouterMain({ children, config }) {
  const positionHandler = usePositionHandler(config)
  const view = useSelector(viewSelector)

  const commonProps = { position: positionHandler, config }
  const cookie = Cookies.get('session-mode')

  const theme = useTheme()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const [state, dispatch] = useReducer(reducer, {
    dragActive: false,
    dragEnd: true,
    initial: getMapSize(viewport),
    current: getMapSize(viewport),
    offset: getMapSize(viewport),
    isListVisible: true
  })

  return (
    <AppRouterContext.Provider value={{ state, dispatch }}>
      <MemoryRouter>
        <Snack />
        <Dialog {...commonProps} />

        {positionHandler?.user && Object.values(positionHandler.user).some((i) => i) && (
          <DrawerProduct position={positionHandler} />
        )}

        <PositionHandler {...positionHandler} config={config} />

        {cookie && view !== TYPES.VIEW_TERM && (
          <Grid
            style={{
              position: 'absolute',
              ...(isMobile ? { bottom: 40, zIndex: 103 } : { top: 2, zIndex: 100 }),
              right: isMobile ? 0 : 220
            }}
            onClick={() => {
              Cookies.set('session-mode', '', getDomain(), 0)
              console.log('Cookie removed')
              location.replace('/')
            }}
          >
            <AnonymousMode isMobile={isMobile} />
          </Grid>
        )}

        <Switch>
          <Route
            path="/termos"
            exact
            render={(props) => <Terms language={commonProps.config.language || 'pt'} />}
          />

          <Route exact path="/" render={(props) => <IndexPage {...props} {...commonProps} />} />

          <Route
            // exact
            path="/sacola/:gtins"
            render={(props) => <ShopBasketPage {...props} {...commonProps} />}
          />

          <Route
            // exact
            path={['/produto/:gtin/detalhes', '/produto/:fakeName/:gtin/detalhes']}
            render={(props) => <ProductDetailsPage {...props} {...commonProps} />}
          />

          <Route
            // exact
            path="/loja/:cnpj/produtos"
            render={(props) => <StorePage {...props} {...commonProps} />}
          />

          <Route
            // exact
            path={['/produto/:fakeName/:gtin', '/produto/:gtin']}
            render={(props) => <StoresPage {...props} {...commonProps} />}
          />

          <Route
            exact
            path="/busca"
            render={(props) => <SearchPage {...props} {...commonProps} />}
          />

          <Route
            // exact
            path="/onlineStore"
            render={(props) => <OnlineStoreEmbed {...props} {...commonProps} />}
          />

          <Route
            // exact
            path="/loja/:cnpj"
            render={(props) => {
              return (
                <>
                  {[TYPES.VIEW_STORES].includes(view) && (
                    <StoreInformations {...props} {...commonProps} />
                  )}
                </>
              )
            }}
          />
        </Switch>

        <Route path="*" component={FallbackRouter} />

        {cloneElement(children, { position: positionHandler, config })}
      </MemoryRouter>
    </AppRouterContext.Provider>
  )
}

function FallbackRouter() {
  const history = useHistory()

  const isRouter = routersWhiteList.test(history.location.pathname)
  return isRouter ? null : <Redirect to="/" />
}
